// EP-135 Tier 3 — Manual attendee add modal. Admin+ only; the page
// checks role before opening. The bulk path is `snapshot_attendees`;
// this is the operator edge case (post-notice contract holder who
// would otherwise lose their right to vote).
//
// EP-149 T149-003 (SEC-1 defense in depth): the customer picker used to be
// a raw number Input bound straight to `customer_id`, so any id an operator
// typed (including another account's) was sent as-is; the real security
// boundary is the backend scoping in T149-001, but this picker should never
// have let an operator type an arbitrary id in the first place. Replaced
// with the same account-scoped SearchableDropdown CommitteeMemberFormModal
// already uses: sourced from useOwnerContracts (backed by
// /api/shared_owners, SQL ILIKE search), deduped by customer id, and Save
// only enables once a result is picked, never from freely-typed text.
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal } from '@/components/ui/Modal'
import { Button } from '@/components/ui'
import { SearchableDropdown } from '@/components/ui/SearchableDropdown'
import { useCreateAttendee } from '@/hooks/useGovernance'
import { useOwnerContracts } from '@/hooks/useOwnerContracts'
import { useNotificationStore } from '@/stores'
import { useApiErrorTranslator } from '@/hooks/useApiErrorTranslator'

export interface AttendeeFormModalProps {
  isOpen: boolean
  meetingId: number
  onClose: () => void
}

export function AttendeeFormModal({ isOpen, meetingId, onClose }: AttendeeFormModalProps) {
  const { t } = useTranslation('governance')
  const { t: tc } = useTranslation('common')
  const addToast = useNotificationStore((s) => s.addToast)
  const translateError = useApiErrorTranslator('governance')

  const [customerId, setCustomerId] = useState<number | null>(null)
  const [customerSearch, setCustomerSearch] = useState('')
  const [presence, setPresence] = useState<'absent' | 'remote' | 'in_person' | 'proxy'>('in_person')

  const create = useCreateAttendee(meetingId)
  // Same roll CommitteeMemberFormModal.tsx uses (39-49): a shared owner is
  // excluded from the Meilisearch customer index, so /api/shared_owners
  // (account-scoped SQL ILIKE) is the only search that can find one. One
  // person can hold several units, so dedupe by customer id.
  const ownerContracts = useOwnerContracts(1, undefined, customerSearch || undefined)
  const eligible = (() => {
    const seen = new Map<number, string>()
    for (const lease of ownerContracts.data?.data ?? []) {
      const c = lease.customer
      if (c && !seen.has(c.id)) {
        seen.set(c.id, c.name || [c.first_name ?? '', c.last_name ?? ''].filter(Boolean).join(' ').trim())
      }
    }
    return [...seen.entries()].map(([id, label]) => ({ value: String(id), label }))
  })()

  const handleSave = async () => {
    if (!customerId) return
    try {
      await create.mutateAsync({
        customer_id: customerId,
        // weight_snapshot + unit_snapshot are intentionally omitted —
        // the API defaults them from the snapshot service so the
        // operator never has to type a decimal.
        weight_snapshot: '',
        unit_snapshot: null,
        present: presence !== 'absent',
      })
      setCustomerId(null)
      setCustomerSearch('')
      setPresence('in_person')
      onClose()
    } catch (err) {
      addToast({ type: 'error', message: translateError(err) })
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('attendees.actions.add')}
      size="md"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            {tc('actions.cancel')}
          </Button>
          <Button
            onClick={handleSave}
            disabled={!customerId}
            loading={create.isPending}
            data-testid="btn-save-attendee"
          >
            {tc('actions.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <label className="label">
            <span className="label-text">{t('attendees.labels.customer')}</span>
          </label>
          <SearchableDropdown
            value={customerId != null ? String(customerId) : ''}
            onChange={(v) => setCustomerId(v ? Number(v) : null)}
            onSearch={setCustomerSearch}
            options={eligible}
            placeholder={t('attendees.labels.customer')}
          />
        </div>
        <div className="text-xs text-base-content/60">
          {t('attendees.description')}
        </div>
      </div>
    </Modal>
  )
}